import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FiFileText,
  FiPaperclip,
  FiRefreshCw
} from "react-icons/fi";
import AddModModal from "./AddModModal";
import AddTimelineModal from "./AddTimelineModal";
import AddMaintenanceModal from "./AddMaintenanceModal";
import DocumentViewerModal from "./DocumentViewerModal";
import VehicleSpecsModal from "./VehicleSpecsModal";
import { fetchBuildDocuments } from "../utils/buildDocuments";
import {
  VEHICLE_SPEC_FIELDS,
  normalizeVehicleSpecs
} from "../utils/buildStages";

function GarageEditor({
  carYear,
  setCarYear,
  carMake,
  setCarMake,
  carModel,
  setCarModel,
  purchaseDate,
  setPurchaseDate,
  carStatus,
  setCarStatus,
  hasUnlimitedGarage,
  salePrice,
  setSalePrice,
  saleMileage,
  setSaleMileage,
  saleTitleStatus,
  setSaleTitleStatus,
  salePreferredContact,
  setSalePreferredContact,
  saleClassifiedsLink,
  setSaleClassifiedsLink,
  carDetails,
  setCarDetails,
  oemSpecs,
  setOemSpecs,
  phaseSpecs,
  setPhaseSpecs,
  currentPhaseName,
  modCategories,
  modCategory,
  setModCategory,
  modPartName,
  setModPartName,
  modBrand,
  setModBrand,
  modInstallDate,
  setModInstallDate,
  modInstallMileage,
  setModInstallMileage,
  modPrice,
  setModPrice,
  modLink,
  setModLink,
  addModEntry,
  editingModId,
  carMods,
  moveModUp,
  moveModDown,
  editModEntry,
  openConfirmModal,
  deleteModEntry,
  timelineDate,
  setTimelineDate,
  timelineMod,
  setTimelineMod,
  addTimelineEntry,
  editingTimelineId,
  carTimeline,
  editTimelineEntry,
  deleteTimelineEntry,
  carMaintenance,
  editingMaintenanceId,
  addMaintenanceEntry,
  editMaintenanceEntry,
  deleteMaintenanceEntry,
  setCarImages,
  existingCarImages,
  deleteExistingCarImage,
  showBuildCost,
  setShowBuildCost,
  addOrUpdateGarageCar,
  ownerId,
  username,
  carId,
  isOwner
}) {
  const navigate = useNavigate();

  const canUseBuildBinder =
    Boolean(ownerId) &&
    Boolean(username) &&
    Boolean(carId) &&
    Boolean(isOwner);

  const [modModalOpen, setModModalOpen] = useState(false);
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [modsOpen, setModsOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [vehicleSpecsOpen, setVehicleSpecsOpen] = useState(false);
  const [vehicleSpecsModalOpen, setVehicleSpecsModalOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [buildBinderOpen, setBuildBinderOpen] = useState(false);

  const [binderDocuments, setBinderDocuments] = useState([]);
  const [binderDocumentsLoading, setBinderDocumentsLoading] = useState(false);
  const [binderDocumentsError, setBinderDocumentsError] = useState("");
  const [viewDocument, setViewDocument] = useState(null);

  const normalizedOemSpecs = normalizeVehicleSpecs(oemSpecs);
  const normalizedPhaseSpecs = normalizeVehicleSpecs(phaseSpecs);
  const completedOemSpecs = countCompletedSpecs(normalizedOemSpecs);
  const completedPhaseSpecs = countCompletedSpecs(normalizedPhaseSpecs);

  const loadBinderDocuments = useCallback(async () => {
    if (!canUseBuildBinder) {
      setBinderDocuments([]);
      setBinderDocumentsError("");
      return;
    }

    setBinderDocumentsLoading(true);
    setBinderDocumentsError("");

    try {
      const documents = await fetchBuildDocuments({
        ownerId,
        carId: String(carId)
      });

      setBinderDocuments(Array.isArray(documents) ? documents : []);
    } catch (error) {
      console.log("Linked Build Binder documents failed to load:", error);
      setBinderDocumentsError("Linked documents could not be loaded.");
    } finally {
      setBinderDocumentsLoading(false);
    }
  }, [canUseBuildBinder, ownerId, carId]);

  useEffect(() => {
    loadBinderDocuments();
  }, [loadBinderDocuments]);

  const maintenanceDocumentsById = useMemo(() => {
    const documentsById = {};

    binderDocuments.forEach((documentItem) => {
      if (
        documentItem?.linkedType !== "maintenance" ||
        !documentItem.linkedItemId
      ) {
        return;
      }

      const linkedItemId = String(documentItem.linkedItemId);

      if (!documentsById[linkedItemId]) {
        documentsById[linkedItemId] = [];
      }

      documentsById[linkedItemId].push(documentItem);
    });

    return documentsById;
  }, [binderDocuments]);

  const modDocumentsById = useMemo(() => {
    const documentsById = {};

    binderDocuments.forEach((documentItem) => {
      if (
        documentItem?.linkedType !== "mod" ||
        !documentItem.linkedItemId
      ) {
        return;
      }

      const linkedItemId = String(documentItem.linkedItemId);

      if (!documentsById[linkedItemId]) {
        documentsById[linkedItemId] = [];
      }

      documentsById[linkedItemId].push(documentItem);
    });

    return documentsById;
  }, [binderDocuments]);

  const openBuildBinder = () => {
    navigate(`/user/${username}/car/${String(carId)}/binder`);
  };

  const openBuildBinderUpload = () => {
    navigate(`/user/${username}/car/${String(carId)}/binder?upload=1`);
  };

  const renderLinkedDocuments = (linkedDocuments) => {
    if (!linkedDocuments || linkedDocuments.length === 0) return null;

    const hasMultipleDocuments = linkedDocuments.length > 1;

    return (
      <div style={linkedDocumentsSectionStyle}>
        <div style={linkedDocumentsHeaderStyle}>
          <span>
            <FiPaperclip aria-hidden="true" />
            {hasMultipleDocuments ? "Attached Documents" : "Attached Document"}
          </span>

          {hasMultipleDocuments && (
            <span style={linkedDocumentsCountStyle}>
              {linkedDocuments.length}
            </span>
          )}
        </div>

        <div style={linkedDocumentListStyle}>
          {linkedDocuments.map((documentItem) => (
            <button
              key={documentItem.id}
              type="button"
              onClick={() => setViewDocument(documentItem)}
              style={linkedDocumentButtonStyle}
            >
              <FiFileText aria-hidden="true" />

              <span style={linkedDocumentTextStyle}>
                <strong style={linkedDocumentTitleStyle}>
                  {documentItem.title || "Untitled document"}
                </strong>

                <small style={linkedDocumentMetaStyle}>
                  {getBuildDocumentFileTypeLabel(documentItem.fileType)}
                  {documentItem.category
                    ? ` • ${documentItem.category}`
                    : ""}
                </small>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={sectionContentStyle}>
      <div style={quickActionsStyle}>
        <div style={quickActionsTitleStyle}>Quick Actions</div>

        <div style={quickActionsRowStyle}>
          <button
            type="button"
            onClick={() => setModModalOpen(true)}
            style={actionButtonStyle}
          >
            + Add Mod
          </button>

          <button
            type="button"
            onClick={() => setTimelineModalOpen(true)}
            style={actionButtonStyle}
          >
            + Add Timeline
          </button>

          <button
            type="button"
            onClick={() => document.getElementById("car-photo-upload")?.click()}
            style={actionButtonStyle}
          >
            + Add Photos
          </button>

          <button
            type="button"
            onClick={() => setMaintenanceModalOpen(true)}
            style={actionButtonStyle}
          >
            + Add Maintenance
          </button>

          {canUseBuildBinder && (
            <button
              type="button"
              onClick={openBuildBinderUpload}
              style={actionButtonStyle}
            >
              + Add Document
            </button>
          )}
        </div>
      </div>

      {canUseBuildBinder && binderDocumentsLoading && (
        <div style={linkedDocumentsStatusStyle}>
          Loading linked documents...
        </div>
      )}

      {canUseBuildBinder && binderDocumentsError && (
        <div style={linkedDocumentsStatusStyle}>
          <span>{binderDocumentsError}</span>

          <button
            type="button"
            onClick={loadBinderDocuments}
            style={linkedDocumentsRetryButtonStyle}
          >
            <FiRefreshCw aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setModsOpen(!modsOpen)}
        style={sectionToggleButtonStyle}
      >
        <span>Mods ({carMods.length})</span>
        <span>{modsOpen ? "▲" : "▼"}</span>
      </button>

      <AddModModal
        isOpen={modModalOpen}
        modCategories={modCategories}
        modCategory={modCategory}
        setModCategory={setModCategory}
        modPartName={modPartName}
        setModPartName={setModPartName}
        modBrand={modBrand}
        setModBrand={setModBrand}
        modInstallDate={modInstallDate}
        setModInstallDate={setModInstallDate}
        modInstallMileage={modInstallMileage}
        setModInstallMileage={setModInstallMileage}
        modPrice={modPrice}
        setModPrice={setModPrice}
        modLink={modLink}
        setModLink={setModLink}
        editingModId={editingModId}
        onSave={(modPayload) => {
          const saved = addModEntry(modPayload);

          if (saved) {
            setModModalOpen(false);
          }
        }}
        onCancel={() => setModModalOpen(false)}
      />

      {modsOpen && (
        <>
          {carMods.length > 0 && (
            <div style={garagePreviewStyle}>
              {carMods.map((mod, index) => {
                const linkedDocuments =
                  modDocumentsById[String(mod.id)] || [];

                return (
                  <div key={mod.id} style={garageCarStyle}>
                    <strong>{mod.partName}</strong>
                    <p>{mod.category}</p>
                    {mod.brand && <p>Brand: {mod.brand}</p>}
                    {mod.installDate && <p>Installed: {mod.installDate}</p>}
                    {mod.installMileage && <p>Mileage: {mod.installMileage}</p>}
                    {mod.link && <p>{mod.link}</p>}

                    {renderLinkedDocuments(linkedDocuments)}

                    <div style={buttonRowStyle}>
                      <button
                        type="button"
                        onClick={() => moveModUp(index)}
                        disabled={index === 0}
                        style={editButtonStyle}
                      >
                        ↑ Move Up
                      </button>

                      <button
                        type="button"
                        onClick={() => moveModDown(index)}
                        disabled={index === carMods.length - 1}
                        style={editButtonStyle}
                      >
                        ↓ Move Down
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          editModEntry(mod);
                          setModModalOpen(true);
                        }}
                        style={editButtonStyle}
                      >
                        Edit Mod
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openConfirmModal({
                            title: "Delete Mod",
                            message: `Are you sure you want to delete "${mod.partName}"? This action cannot be undone.`,
                            confirmText: "Delete Mod",
                            onConfirm: () => deleteModEntry(mod.id)
                          })
                        }
                        style={removeButtonStyle}
                      >
                        Delete Mod
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => setMaintenanceOpen(!maintenanceOpen)}
        style={sectionToggleButtonStyle}
      >
        <span>Maintenance ({carMaintenance.length})</span>
        <span>{maintenanceOpen ? "▲" : "▼"}</span>
      </button>

      {maintenanceOpen && (
        <>
          {carMaintenance.length > 0 && (
            <div style={garagePreviewStyle}>
              {carMaintenance.map((item) => {
                const linkedDocuments =
                  maintenanceDocumentsById[String(item.id)] || [];

                return (
                  <div key={item.id} style={timelineEntryStyle}>
                    <strong>{item.serviceType || "Maintenance"}</strong>

                    {item.serviceDate && <p>Date: {item.serviceDate}</p>}

                    {item.mileage !== null &&
                      item.mileage !== undefined &&
                      item.mileage !== "" && <p>Mileage: {item.mileage}</p>}

                    {item.performedBy && <p>Performed By: {item.performedBy}</p>}
                    {item.serviceProvider && <p>Provider: {item.serviceProvider}</p>}

                    {item.totalCost !== null && item.totalCost !== undefined && (
                      <p>Total Cost: ${item.totalCost}</p>
                    )}

                    {item.partsUsed?.length > 0 && (
                      <div>
                        <p style={{ marginBottom: "6px" }}>Parts Used:</p>

                        {item.partsUsed.map((part) => (
                          <div
                            key={part.id || part.partName}
                            style={{ marginLeft: "10px" }}
                          >
                            <strong>
                              {part.brand ? `${part.brand} — ` : ""}
                              {part.partName}
                            </strong>

                            {part.partNumber && <p>Part #: {part.partNumber}</p>}

                            {part.cost !== null && part.cost !== undefined && (
                              <p>${part.cost}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {item.nextServiceDate && (
                      <p>Next Service Date: {item.nextServiceDate}</p>
                    )}

                    {item.nextServiceMileage !== null &&
                      item.nextServiceMileage !== undefined &&
                      item.nextServiceMileage !== "" && (
                        <p>Next Service Mileage: {item.nextServiceMileage}</p>
                      )}

                    {item.receiptUrl && (
                      <p>
                        <a
                          href={item.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={editButtonStyle}
                        >
                          View Receipt
                        </a>
                      </p>
                    )}

                    {item.notes && <p>{item.notes}</p>}

                    {renderLinkedDocuments(linkedDocuments)}

                    <div style={buttonRowStyle}>
                      <button
                        type="button"
                        onClick={() => {
                          editMaintenanceEntry(item);
                          setMaintenanceModalOpen(true);
                        }}
                        style={editButtonStyle}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          openConfirmModal({
                            title: "Delete Maintenance",
                            message:
                              "Are you sure you want to delete this maintenance entry?",
                            confirmText: "Delete Maintenance",
                            onConfirm: () => deleteMaintenanceEntry(item.id)
                          })
                        }
                        style={removeButtonStyle}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <AddMaintenanceModal
        isOpen={maintenanceModalOpen}
        editingMaintenanceId={editingMaintenanceId}
        initialMaintenance={
          editingMaintenanceId
            ? carMaintenance.find((item) => item.id === editingMaintenanceId)
            : null
        }
        isSaving={false}
        onSave={(maintenancePayload) => {
          const saved = addMaintenanceEntry(maintenancePayload);

          if (saved) {
            setMaintenanceModalOpen(false);
          }
        }}
        onCancel={() => setMaintenanceModalOpen(false)}
      />

      {canUseBuildBinder && (
        <>
          <button
            type="button"
            onClick={() => setBuildBinderOpen(!buildBinderOpen)}
            style={sectionToggleButtonStyle}
          >
            <span>Build Binder</span>
            <span>{buildBinderOpen ? "▲" : "▼"}</span>
          </button>

          {buildBinderOpen && (
            <div style={garagePreviewStyle}>
              <div style={garageCarStyle}>
                <p style={{ marginTop: 0 }}>
                  Store receipts, warranties, invoices, and other private vehicle records.
                </p>

                <button
                  type="button"
                  onClick={openBuildBinder}
                  style={editButtonStyle}
                >
                  View Build Binder
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => setVehicleSpecsOpen(!vehicleSpecsOpen)}
        style={sectionToggleButtonStyle}
        aria-expanded={vehicleSpecsOpen}
      >
        <span>Vehicle Specs</span>
        <span>{vehicleSpecsOpen ? "▲" : "▼"}</span>
      </button>

      {vehicleSpecsOpen && (
        <div style={garagePreviewStyle}>
          <div style={garageCarStyle}>
            <div style={specSummaryGridStyle}>
              <div style={specSummaryCardStyle}>
                <strong>OEM Specs</strong>
                <p style={specSummaryTextStyle}>
                  {completedOemSpecs} of {VEHICLE_SPEC_FIELDS.length} completed
                </p>
              </div>

              <div style={specSummaryCardStyle}>
                <strong>
                  Current Build Phase
                  {currentPhaseName ? ` — ${currentPhaseName}` : ""}
                </strong>
                <p style={specSummaryTextStyle}>
                  {completedPhaseSpecs} of {VEHICLE_SPEC_FIELDS.length} completed
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setVehicleSpecsModalOpen(true)}
              style={editButtonStyle}
            >
              Edit Vehicle Specs
            </button>
          </div>
        </div>
      )}

      <VehicleSpecsModal
        isOpen={vehicleSpecsModalOpen}
        onClose={() => setVehicleSpecsModalOpen(false)}
        oemSpecs={oemSpecs}
        setOemSpecs={setOemSpecs}
        phaseSpecs={phaseSpecs}
        setPhaseSpecs={setPhaseSpecs}
        currentPhaseName={currentPhaseName}
      />

      <button
        type="button"
        onClick={() => setTimelineOpen(!timelineOpen)}
        style={sectionToggleButtonStyle}
      >
        <span>Timeline ({carTimeline.length})</span>
        <span>{timelineOpen ? "▲" : "▼"}</span>
      </button>

      <AddTimelineModal
        isOpen={timelineModalOpen}
        timelineDate={timelineDate}
        setTimelineDate={setTimelineDate}
        timelineMod={timelineMod}
        setTimelineMod={setTimelineMod}
        editingTimelineId={editingTimelineId}
        onSave={() => {
          addTimelineEntry();
          setTimelineModalOpen(false);
        }}
        onCancel={() => setTimelineModalOpen(false)}
      />

      {timelineOpen && (
        <>
          {carTimeline.length > 0 && (
            <div style={garagePreviewStyle}>
              {carTimeline.map((entry) => (
                <div key={entry.id} style={timelineEntryStyle}>
                  <strong>{entry.date}</strong>
                  <p style={{ marginTop: "6px" }}>{entry.mod}</p>

                  <div style={buttonRowStyle}>
                    <button
                      type="button"
                      onClick={() => {
                        editTimelineEntry(entry);
                        setTimelineModalOpen(true);
                      }}
                      style={editButtonStyle}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        openConfirmModal({
                          title: "Delete Timeline Entry",
                          message:
                            "Are you sure you want to delete this timeline entry?",
                          confirmText: "Delete Entry",
                          onConfirm: () => deleteTimelineEntry(entry.id)
                        })
                      }
                      style={removeButtonStyle}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => setPhotosOpen(!photosOpen)}
        style={sectionToggleButtonStyle}
      >
        <span>Photos ({existingCarImages.length})</span>
        <span>{photosOpen ? "▲" : "▼"}</span>
      </button>

      <input
        id="car-photo-upload"
        type="file"
        multiple
        accept="image/*"
        onChange={(e) => setCarImages(Array.from(e.target.files))}
        style={{ display: "none" }}
      />

      {photosOpen && existingCarImages.length > 0 && (
        <div style={currentPhotosGridStyle}>
          {existingCarImages.map((imageUrl) => (
            <div key={imageUrl} style={currentPhotoCardStyle}>
              <button
                type="button"
                onClick={() =>
                  openConfirmModal({
                    title: "Delete Photo",
                    message:
                      "Are you sure you want to delete this photo? This action cannot be undone.",
                    confirmText: "Delete Photo",
                    onConfirm: () => deleteExistingCarImage(imageUrl)
                  })
                }
                style={photoDeleteIconStyle}
              >
                🗑
              </button>

              <img src={imageUrl} alt="Build" style={currentPhotoStyle} />
            </div>
          ))}
        </div>
      )}

      <div style={settingsCardStyle}>
        <div style={settingsTitleStyle}>Public Display</div>

        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={showBuildCost}
            onChange={(e) => setShowBuildCost(e.target.checked)}
          />

          Show build cost on public build page
        </label>
      </div>

      <button type="button" onClick={addOrUpdateGarageCar} style={saveBuildButtonStyle}>
        Save Current Phase
      </button>

      <DocumentViewerModal
        isOpen={Boolean(viewDocument)}
        onClose={() => setViewDocument(null)}
        documentItem={viewDocument}
      />
    </div>
  );
}

function countCompletedSpecs(specs) {
  const normalizedSpecs = normalizeVehicleSpecs(specs);

  return VEHICLE_SPEC_FIELDS.filter(({ key }) => normalizedSpecs[key]).length;
}

function getBuildDocumentFileTypeLabel(fileType) {
  if (fileType === "application/pdf") return "PDF";
  if (fileType === "image/jpeg") return "JPG";
  if (fileType === "image/png") return "PNG";

  return "FILE";
}

const sectionContentStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "18px"
};

const editButtonStyle = {
  backgroundColor: "#333",
  color: "white",
  border: "1px solid #444",
  padding: "8px 12px",
  borderRadius: "8px",
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-block",
  fontWeight: "bold"
};

const removeButtonStyle = {
  backgroundColor: "#600",
  color: "white",
  border: "none",
  padding: "8px 12px",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "bold"
};

const garagePreviewStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const garageCarStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #2a2a2a",
  padding: "14px",
  borderRadius: "12px"
};

const timelineEntryStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #2a2a2a",
  borderLeft: "4px solid #e10600",
  padding: "14px",
  borderRadius: "12px"
};

const specSummaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "10px",
  marginBottom: "12px"
};

const specSummaryCardStyle = {
  backgroundColor: "#171717",
  border: "1px solid #303030",
  borderRadius: "10px",
  padding: "12px",
  boxSizing: "border-box"
};

const specSummaryTextStyle = {
  color: "#aaa",
  margin: "6px 0 0",
  fontSize: "13px"
};

const linkedDocumentsSectionStyle = {
  marginTop: "10px",
  padding: "10px",
  backgroundColor: "#171717",
  border: "1px solid #303030",
  borderRadius: "10px",
  boxSizing: "border-box"
};

const linkedDocumentsHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  color: "white",
  fontSize: "12px",
  fontWeight: "900",
  marginBottom: "8px"
};

const linkedDocumentsCountStyle = {
  minWidth: "22px",
  borderRadius: "999px",
  padding: "2px 7px",
  backgroundColor: "#333",
  color: "#ddd",
  border: "1px solid #444",
  fontSize: "11px",
  fontWeight: "900",
  textAlign: "center",
  boxSizing: "border-box"
};

const linkedDocumentListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "7px"
};

const linkedDocumentButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "flex-start",
  gap: "9px",
  backgroundColor: "#202020",
  color: "white",
  border: "1px solid #363636",
  borderRadius: "9px",
  padding: "9px 10px",
  cursor: "pointer",
  textAlign: "left",
  boxSizing: "border-box",
  overflowWrap: "anywhere"
};

const linkedDocumentTextStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  minWidth: 0,
  flex: 1,
  overflowWrap: "anywhere"
};

const linkedDocumentTitleStyle = {
  color: "white",
  fontSize: "13px",
  lineHeight: "1.3",
  overflowWrap: "anywhere"
};

const linkedDocumentMetaStyle = {
  color: "#aaa",
  fontSize: "12px",
  lineHeight: "1.3",
  overflowWrap: "anywhere"
};

const linkedDocumentsStatusStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  backgroundColor: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "10px 12px",
  color: "#aaa",
  fontSize: "13px",
  lineHeight: "1.45",
  boxSizing: "border-box"
};

const linkedDocumentsRetryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  backgroundColor: "#333",
  color: "white",
  border: "1px solid #444",
  borderRadius: "999px",
  padding: "7px 10px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "bold"
};

const currentPhotosGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "10px"
};

const currentPhotoCardStyle = {
  position: "relative",
  backgroundColor: "#1f1f1f",
  border: "1px solid #2a2a2a",
  padding: "10px",
  borderRadius: "12px",
  overflow: "hidden"
};

const currentPhotoStyle = {
  width: "100%",
  aspectRatio: "1 / 1",
  objectFit: "cover",
  borderRadius: "10px"
};

const saveBuildButtonStyle = {
  backgroundColor: "#e10600",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "14px 18px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "12px",
  width: "100%",
  marginTop: "12px"
};

const photoDeleteIconStyle = {
  position: "absolute",
  top: "16px",
  right: "16px",
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "none",
  backgroundColor: "rgba(0,0,0,0.75)",
  color: "white",
  cursor: "pointer",
  fontSize: "18px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  transition: "0.2s"
};

const buttonRowStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  marginTop: "10px"
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "15px"
};

const sectionToggleButtonStyle = {
  width: "100%",
  backgroundColor: "#1f1f1f",
  color: "white",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "14px",
  fontWeight: "bold",
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const quickActionsStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "14px",
  padding: "14px",
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const quickActionsTitleStyle = {
  fontSize: "12px",
  fontWeight: "bold",
  opacity: 0.75,
  textTransform: "uppercase",
  letterSpacing: "0.6px"
};

const quickActionsRowStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap"
};

const actionButtonStyle = {
  backgroundColor: "#e10600",
  color: "white",
  border: "none",
  borderRadius: "999px",
  padding: "10px 14px",
  fontWeight: "bold",
  cursor: "pointer"
};

const settingsCardStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "14px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const settingsTitleStyle = {
  fontSize: "12px",
  fontWeight: "bold",
  opacity: 0.75,
  textTransform: "uppercase",
  letterSpacing: "0.6px"
};

export default GarageEditor;